import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { buildApiUrl, fetchWithAuth } from '@/utils/apiClient';
import { DEFAULT_PAGE_LIMIT, unwrapPaginatedItems } from '../../../hooks/useApiQuery';
import EncounterForm from '@/components/therapist/components/EncounterForm';
import { useCptAddonSelection } from '@/hooks/useCptAddonSelection';

import { todayLocalDateString } from './../../../utils/dateUtils';
import { logger } from '../../../utils/logger';
import { PhoneInput } from '../../../components/ui/PhoneInput';

function PatientEncounterForms({
  showPatientForm,
  setShowPatientForm,
  showEncounterForm,
  setShowEncounterForm,
  selectedPatientForNote,
  setSelectedPatientForNote,
  setCurrentPatient,
  sendMessage,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- passed by parent, reserved for future use
  patients: _patients,
  setPatients,
}) {
  const [locations, setLocations] = useState([]);
  const [cptCodes, setCptCodes] = useState([]);
  const [appointmentTypes, setAppointmentTypes] = useState([]);
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState(null);
  const [customAddons, setCustomAddons] = useState<Array<{ code: string; description: string; units: number }>>([]); 

  const [newPatient, setNewPatient] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
  });

  const [newEncounter, setNewEncounter] = useState({
    date: todayLocalDateString(),
    time: '09:00',
    location_id: '',
    appointment_type_id: '',
    appointment_type_variant_id: '',
    cpt_code: '',
    duration_minutes: 50,
  });

  const handleDurationChange = useCallback(
    (d: number) => setNewEncounter((prev) => ({ ...prev, duration_minutes: d })),
    [],
  );

  const cptAddon = useCptAddonSelection({
    appointmentTypes,
    selectedTypeId: newEncounter.appointment_type_id,
    durationMinutes: newEncounter.duration_minutes,
    onDurationChange: handleDurationChange,
  });

  useEffect(() => {
    if (showPatientForm || showEncounterForm) {
      loadLocations();
      loadCPTCodes();
      loadAppointmentTypes();
    }
  }, [showPatientForm, showEncounterForm]);

  const loadLocations = async () => {
    try {
      const response = await fetchWithAuth(buildApiUrl('/api/locations'));
      if (response.ok) {
        setLocations(await response.json());
      }
    } catch (err) {
      logger.error('Failed to load locations:', err);
    }
  };

  const loadCPTCodes = async () => {
    try {
      const response = await fetchWithAuth(buildApiUrl('/api/patients/cpt-codes'));
      if (response.ok) {
        const data = await response.json();
        setCptCodes(data.cpt_codes || []);
      }
    } catch (err) {
      logger.error('Failed to load CPT codes:', err);
    }
  };

  const loadAppointmentTypes = async () => {
    try {
      const response = await fetchWithAuth(
        buildApiUrl('/api/calendar/appointments/metadata/appointment-types'),
      );
      if (response.ok) {
        setAppointmentTypes(await response.json());
      }
    } catch (err) {
      logger.error('Failed to load appointment types:', err);
    }
  };

  const handleAppointmentTypeChange = () => {
    cptAddon.resetAddonState();
  };

  const handleCreatePatient = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    setFormError(null);

    try {
      const response = await fetchWithAuth(buildApiUrl('/api/patients/quick-create'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newPatient),
      });

      if (response.ok) {
        const data = await response.json();
        setSelectedPatientForNote(data);
        setCurrentPatient({ id: data.id, name: data.name });
        setShowPatientForm(false);
        setShowEncounterForm(true);

        const patientsResponse = await fetchWithAuth(
          buildApiUrl(`/api/patients?limit=${DEFAULT_PAGE_LIMIT}`),
        );
        if (patientsResponse.ok) {
          const patientsData = await patientsResponse.json();
          setPatients(unwrapPaginatedItems(patientsData));
        }

        setNewPatient({ first_name: '', last_name: '', email: '', phone: '' });
        if (data.email_sent === false) {
          toast.warning(
            "Patient created, but the portal invite email could not be sent. You can resend it later from the patient's profile.",
          );
        }
      } else {
        const error = await response.json();
        setFormError(error.detail || 'Failed to create patient');
      }
    } catch (err) {
      setFormError('Failed to create patient. Please try again.');
      logger.error(err);
    } finally {
      setFormLoading(false);
    }
  };

  const handleCreateEncounter = async (e) => {
    e.preventDefault();
    // The stale note-driven quick-create path is retired. Notes can be written
    // without first creating an encounter; use the calendar to schedule real
    // appointments.
    setFormError(
      'Creating an appointment from this note panel is no longer supported. '
      + 'Use "Skip Encounter" to write a note without an appointment, or use '
      + 'the calendar to schedule the appointment first.'
    );
    setFormLoading(false);
  };

  const handleSkipEncounter = () => {
    setCurrentPatient({
      id: selectedPatientForNote.id,
      name: selectedPatientForNote.name,
    });
    setShowEncounterForm(false);
    cptAddon.resetAddonState();
    setCustomAddons([]);
    sendMessage(`Write note for ${selectedPatientForNote.name} without encounter`);
  };

  return (
    <>
      {showPatientForm && (
        <div className="px-4 mb-4">
          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-lg border border-gray-300 dark:border-gray-600">
            <h3 className="text-lg font-semibold mb-3 text-gray-900 dark:text-white">
              Create New Client
            </h3>
            {formError && (
              <div className="mb-3 p-2 bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-200 rounded">
                {formError}
              </div>
            )}
            <form onSubmit={handleCreatePatient} className="space-y-2">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    First Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={newPatient.first_name}
                    onChange={(e) =>
                      setNewPatient({ ...newPatient, first_name: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Last Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={newPatient.last_name}
                    onChange={(e) =>
                      setNewPatient({ ...newPatient, last_name: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={newPatient.email}
                  onChange={(e) => setNewPatient({ ...newPatient, email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Phone
                </label>
                <PhoneInput
                  value={newPatient.phone}
                  onChange={(v) => setNewPatient({ ...newPatient, phone: v })}
                />
              </div>
              <div className="flex gap-2 mt-3">
                <button
                  type="submit"
                  disabled={formLoading}
                  className="flex-1 px-4 py-2 bg-blue-500 hover:bg-blue-600 disabled:bg-gray-400 text-white rounded-lg transition-colors"
                >
                  {formLoading ? 'Creating...' : 'Create Patient'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowPatientForm(false);
                    setFormError(null);
                    setNewPatient({ first_name: '', last_name: '', email: '', phone: '' });
                  }}
                  className="px-4 py-2 bg-gray-300 hover:bg-gray-400 dark:bg-gray-600 dark:hover:bg-gray-500 text-gray-900 dark:text-white rounded-lg transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showEncounterForm && selectedPatientForNote && (
        <EncounterForm
          selectedPatientForNote={selectedPatientForNote}
          newEncounter={newEncounter}
          setNewEncounter={setNewEncounter}
          locations={locations}
          cptCodes={cptCodes}
          appointmentTypes={appointmentTypes}
          selectedAddons={cptAddon.selectedAddons}
          onAddonToggle={cptAddon.handleAddonToggle}
          onAppointmentTypeChange={handleAppointmentTypeChange}
          customAddons={customAddons}
          setCustomAddons={setCustomAddons}
          customCptMode={cptAddon.customCptMode}
          onToggleCustomCpt={cptAddon.setCustomCptMode}
          primaryCptCodes={cptAddon.primaryCptCodes}
          addonCptCodes={cptAddon.addonCptCodes}
          totalAddonFee={cptAddon.totalAddonFee}
          allowedDurations={cptAddon.allowedDurations}
          formError={formError}
          formLoading={formLoading}
          onSubmit={handleCreateEncounter}
          onSkip={handleSkipEncounter}
          onCancel={() => {
            setShowEncounterForm(false);
            setSelectedPatientForNote(null);
            cptAddon.resetAddonState();
            setCustomAddons([]);
            setFormError(null);
          }}
        />
      )}
    </>
  );
}

export default PatientEncounterForms;
